import React from 'react'
import {Box, BoxProps, Flex, Text, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {Picture} from '_uiComponents'
import {usePageDesigner} from '_customHooks'
import {updateCloudinaryPDImage} from '_utils'
import {MeasurementsSection} from '_types'

interface MeasurementsTabProps {
    section?: MeasurementsSection
}

/**
 * @name MeasurementsTab
 *
 * @component
 *
 * @description
 * Renders the "Measurements" tab content inside the SizeGuide drawer: a title, an
 * intro text and one or more detail blocks (image + list of measurement items).
 *
 * @param {MeasurementsTabProps} props - Properties object.
 * @param {string} [props.contentId] - The Page Designer content asset id for this fit's measurements.
 * @param {MeasurementsSection} [props.section] - The measurements section data (title, text, details).
 *
 * @returns {React.FC} - The rendered measurements tab content.
 */
const MeasurementsTab = ({section}: MeasurementsTabProps) => {
    const styles = useMultiStyleConfig('SizeGuide', {}) as Record<string, BoxProps>
    const {globalImageTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()

    if (!section) {
        return null
    }

    const resizedImgMobile = section.imageMobile ? updateCloudinaryPDImage(section.imageMobile, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgTablet = section.imageTablet ? updateCloudinaryPDImage(section.imageTablet, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgDesktop = section.imageDesktop ? updateCloudinaryPDImage(section.imageDesktop, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''

    return (
        <VStack sx={styles.measurementsSection} align="stretch">
            {section.title && <Text sx={styles.sectionTitle}>{section.title}</Text>}
            {section.subtitle && <Text sx={styles.measurementsText}>{section.subtitle}</Text>}
            <Flex sx={styles.measurementsImageWrapper} gap="spacing.2" flexDirection={{base: 'column', '2xl': 'row'}} align="stretch">
                <Box w={{base: '100%', '2xl': '50%'}}>
                    <Picture
                        imagesm={resizedImgMobile || resizedImgTablet || resizedImgDesktop}
                        imagemd={resizedImgTablet || resizedImgDesktop}
                        imagelg={resizedImgDesktop}
                        imagexl={resizedImgDesktop}
                        alt={section.imageAlt || ''}
                        loading="eager"
                    />
                </Box>
                <Box w={{base: '100%', '2xl': '50%'}}>
                    {section.details.map((detail, detailIndex) => (
                        <Flex key={detailIndex} sx={styles.measurementsDetail}>
                            <VStack sx={styles.measurementsContentText} align="stretch">
                                {detail.items.map((item, itemIndex) => (
                                    <Box key={itemIndex} sx={styles.measurementsContentItem}>
                                        <Text as="h4" sx={styles.measurementsItemTitle}>
                                            {item.title}
                                        </Text>
                                        <Text sx={styles.measurementsItemText}>{item.text}</Text>
                                    </Box>
                                ))}
                            </VStack>
                        </Flex>
                    ))}
                </Box>
            </Flex>
        </VStack>
    )
}

export default MeasurementsTab
